import { Info, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfigurationData, ConfigurationTypeEnum, PricingRuleFormData } from '@libs/schemas';

interface ErrorRecord {
  [key: string]: string;
}

interface BasicInfoStepProps {
  formData: Partial<PricingRuleFormData>;
  errors: ErrorRecord;
  onFormDataChange: (updates: Partial<PricingRuleFormData>) => void;
}

const configurationTypes = [
  {
    value: ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX,
    label: 'Single Material',
    description: 'Single material pricing with included days.',
    icon: '📊',
  },
  {
    value: ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING,
    label: 'Flat Rate',
    description: 'Flat rate package with included days and tonnage.',
    icon: '📅',
  },
  {
    value: ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING,
    label: 'Pay As You Go',
    description: 'Daily container rate plus per-ton charges based on actual weight.',
    icon: '📈',
  },
  {
    value: ConfigurationTypeEnum.TONNAGE_PRICING,
    label: 'Tonnage Pricing',
    description:
      'Set per-ton rate with minimum tonnage requirement. Acts as fallback when no specific rules apply.',
    icon: '⚖️',
  },
  {
    value: ConfigurationTypeEnum.AUTO_DISCOUNT,
    label: 'Automatic Discount',
    description: 'Apply discounts automatically based on order conditions.',
    icon: '🎯',
  },
  {
    value: ConfigurationTypeEnum.DISCOUNT,
    label: 'Discount',
    description: 'Create discount rules that require manual application.',
    icon: '🏷️',
  },
];

export function BasicInfoStep({ formData, errors, onFormDataChange }: BasicInfoStepProps) {
  const updateData = (updates: Partial<PricingRuleFormData>) => {
    onFormDataChange(updates);
  };

  const selectedType = configurationTypes.find(
    (type) => type.value === formData.configuration?.type,
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900">Basic Information</h3>
        <p className="text-sm text-gray-600 mt-1">
          Start by naming your rule and choosing what type of pricing logic it should use.
        </p>
      </div>

      {/* Rule Name */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-md">Rule Identity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="ruleName" className="text-sm font-medium">
              Rule Name
            </Label>
            <Input
              id="ruleName"
              placeholder="e.g., Commercial Premium Rate, Holiday Discount, etc."
              value={formData.name || ''}
              onChange={(e) => updateData({ name: e.target.value })}
              className="mt-1.5"
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1.5 dark:text-red-900">{errors.name}</p>
            )}
            <p className="text-xs text-gray-500 mt-1.5">
              Use a clear, descriptive name that explains the rule's purpose
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Type Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-md">Pricing Logic</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="configurationType" className="text-sm font-medium">
              How should this rule calculate pricing?
            </Label>
            <Select
              value={formData.configuration?.type || ''}
              onValueChange={(value: ConfigurationTypeEnum) => {
                updateData({
                  configuration: { type: value, data: {} as ConfigurationData },
                });
              }}
            >
              <SelectTrigger className="mt-1.5 h-12 w-full">
                <SelectValue placeholder="Choose a pricing method..." />
              </SelectTrigger>
              <SelectContent className="w-full">
                {configurationTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value} className="p-3">
                    <div className="flex items-start space-x-3 w-full">
                      <span className="text-lg mt-0.5">{type.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium">{type.label}</span>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{type.description}</p>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedType && (
              <Alert className="mt-3">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>{selectedType.label}:</strong> {selectedType.description}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Rule Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-md">Rule Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="precedence" className="text-sm font-medium">
              Priority Level
            </Label>
            <Input
              id="precedence"
              type="number"
              min="1"
              max="999"
              value={formData.precedence || 50}
              onChange={(e) => updateData({ precedence: parseInt(e.target.value) || 50 })}
              className="mt-1.5 max-w-[120px]"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              Higher numbers = higher priority (1-999). Rules with higher priority are evaluated
              first.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label htmlFor="canCombine" className="text-sm font-medium">
                  Allow Combining
                </Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Let this rule work together with other rules
                </p>
              </div>
              <Switch
                id="canCombine"
                checked={formData.canCombine !== false}
                onCheckedChange={(checked) => updateData({ canCombine: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label htmlFor="active" className="text-sm font-medium">
                  Active Rule
                </Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Apply this rule to new orders immediately
                </p>
              </div>
              <Switch
                id="active"
                checked={formData.active !== false}
                onCheckedChange={(checked) => updateData({ active: checked })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Validation Alert */}
      {(!formData.name || !formData.configuration?.type) && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please provide a rule name and select a pricing method to continue.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
